/**
 * Services de la présence sur chantier.
 *
 * Pointer l'arrivée ou le départ est N2 et ouvert à tout rôle : c'est la base
 * qui vérifie que le compte est rattaché à un salarié et que ce salarié est
 * affecté au chantier (`pointer_arrivee`). L'heure est celle du serveur ; le
 * service ne la transmet pas, et ne pourrait pas la fausser.
 *
 * Lire les présences de l'équipe est réservé au bureau.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type { Attendance } from "./types.js";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ");

export const clockInInput = z.object({
  confirmationId: z.string().optional(),
  projectId: z.string().uuid("Identifiant de chantier attendu"),
});

export const listAttendanceInput = z.object({
  confirmationId: z.string().optional(),
  from: isoDate,
  to: isoDate,
});

export type ClockInInput = z.infer<typeof clockInInput>;
export type ListAttendanceInput = z.infer<typeof listAttendanceInput>;

export interface AttendanceRepository {
  /** Les présences du jour du salarié rattaché au compte. */
  mine(ctx: ExecutionContext): Promise<Attendance[]>;
  clockIn(ctx: ExecutionContext, projectId: string): Promise<void>;
  clockOut(ctx: ExecutionContext): Promise<void>;
  /** Bornes incluses, sur la journée de travail. */
  list(ctx: ExecutionContext, from: string, to: string): Promise<Attendance[]>;
}

export function createAttendanceServices(repo: AttendanceRepository) {
  const maPresence = defineService<Record<string, never>, Attendance[]>({
    name: "ma_presence_du_jour",
    level: ActionLevel.Read,
    handler: (ctx) => repo.mine(ctx),
  });

  const pointerArrivee = defineService<ClockInInput, Attendance[]>({
    name: "pointer_arrivee",
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input) => {
      await repo.clockIn(ctx, input.projectId);
      return repo.mine(ctx);
    },
  });

  const pointerDepart = defineService<Record<string, never>, Attendance[]>({
    name: "pointer_depart",
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx) => {
      await repo.clockOut(ctx);
      return repo.mine(ctx);
    },
  });

  const listerPresences = defineService<ListAttendanceInput, Attendance[]>({
    name: "lister_presences",
    level: ActionLevel.Read,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) => repo.list(ctx, input.from, input.to),
  });

  return { maPresence, pointerArrivee, pointerDepart, listerPresences };
}

export type AttendanceServices = ReturnType<typeof createAttendanceServices>;
